import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import RouteActionDialog from "@/components/ui/RouteActionDialog.jsx";
import {
  DEFAULT_EXPORT_FORMAT,
  exportFormatsForVessel,
  initialExportFormat,
} from "@/config/routeExportFormats";

export default function RouteActionsMenu({
  routeDeleteOpen,
  routeDeleteCandidate,
  getRouteDisplayName,
  onCloseDeleteDialog,
  onDeleteConfirm,
  routeActionAnchorEl,
  routeActionTarget,
  onCloseRouteActionMenu,
  onRouteActionMenuExited,
  onSetRouteState,
  onSaveDraft,
  onRenameRoute,
  onOpenDeleteFromAction,
  onExportRoute,
  onDialogClose,
  activeVoyage = null,
}) {
  const [routeRenameOpen, setRouteRenameOpen] = useState(false);
  const [routeRenameTarget, setRouteRenameTarget] = useState(null);
  const [routeRenameName, setRouteRenameName] = useState("");
  const [routeRenameSaving, setRouteRenameSaving] = useState(false);
  const [routeRenameError, setRouteRenameError] = useState("");
  const [routeSaveOpen, setRouteSaveOpen] = useState(false);
  const [routeSaveTarget, setRouteSaveTarget] = useState(null);
  const [routeSaveName, setRouteSaveName] = useState("");
  const [routeSaveVoyageName, setRouteSaveVoyageName] = useState("");
  const [routeSaveMode, setRouteSaveMode] = useState("add");
  const [routeSaveSaving, setRouteSaveSaving] = useState(false);
  const [routeExportOpen, setRouteExportOpen] = useState(false);
  const [routeExportTarget, setRouteExportTarget] = useState(null);
  const [routeExportName, setRouteExportName] = useState("");
  const [routeExportFormat, setRouteExportFormat] = useState(DEFAULT_EXPORT_FORMAT);
  const [routeExportSaving, setRouteExportSaving] = useState(false);
  const [routeExportError, setRouteExportError] = useState("");

  // Frozen at open: the save itself activates the new voyage while the dialog is still up.
  const [routeSaveActiveVoyage, setRouteSaveActiveVoyage] = useState(null);
  useEffect(() => {
    if (!routeSaveOpen) return;
    const rawName = routeSaveTarget?.route?.name ?? "Suggested Route";
    const baseName = String(rawName).replace(/\s*\(unsaved\)\s*$/i, "").trim() || "Suggested Route";
    setRouteSaveName(baseName);
    setRouteSaveVoyageName(routeSaveTarget?.route?.suggestedVoyageName ?? "");
    // Drafts planned for a new voyage carry no voyage id.
    const draftVoyageId = Number(routeSaveTarget?.route?.voyageId);
    setRouteSaveMode(routeSaveActiveVoyage && draftVoyageId > 0 ? "add" : "new");
    setRouteSaveSaving(false);
  }, [routeSaveOpen, routeSaveTarget, routeSaveActiveVoyage]);

  const routeSaveVesselName = routeSaveTarget?.vessel?.name ?? "";

  useEffect(() => {
    if (!routeRenameOpen) return;
    const rawName = routeRenameTarget?.route?.name ?? "";
    setRouteRenameName(String(rawName).trim());
    setRouteRenameSaving(false);
    setRouteRenameError("");
  }, [routeRenameOpen, routeRenameTarget]);

  useEffect(() => {
    if (!routeExportOpen) return;
    const rawName = routeExportTarget?.route?.name ?? "Route";
    const baseName = String(rawName).trim() || "Route";
    setRouteExportName(baseName);
    setRouteExportSaving(false);
    setRouteExportError("");
  }, [routeExportOpen, routeExportTarget]);

  // Drafts rename/export via their store entity, DB routes via their id.
  const routeActionTargetUsable =
    routeActionTarget?.route?.source === "draft" ||
    Number.isFinite(Number(routeActionTarget?.route?.id));
  const routeRenameAvailable = routeActionTargetUsable;
  const routeExportAvailable = routeActionTargetUsable;

  return (
    <>
      <Dialog
        open={routeDeleteOpen}
        onClose={onCloseDeleteDialog}
        slotProps={{
          transition: {
            onExited: () => onOpenDeleteFromAction(null),
          },
        }}
        disableRestoreFocus
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Delete Route
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Delete route{" "}
            <strong>
              {routeDeleteCandidate
                ? getRouteDisplayName(routeDeleteCandidate.route)
                : ""}
            </strong>
            ?
          </DialogContentText>
          <DialogContentText
            sx={{ mt: 1, fontSize: 13, color: "text.secondary" }}
          >
            {routeDeleteCandidate?.route?.source === "draft"
              ? "This will remove the temporary suggested route."
              : "This will also remove all waypoints of this route."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={onCloseDeleteDialog}
            sx={{
              textTransform: "none",
              minWidth: 86,
              borderColor: "rgba(0,79,125,0.35)",
              color: "#004f7d",
              "&:hover": {
                borderColor: "#004f7d",
                backgroundColor: "rgba(0,79,125,0.06)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteConfirm}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={routeActionAnchorEl}
        open={Boolean(routeActionAnchorEl)}
        onClose={onCloseRouteActionMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ transition: { onExited: onRouteActionMenuExited } }}
      >
        <MenuItem
          disabled={!routeRenameAvailable}
          sx={{
            fontSize: 13,
            color: routeRenameAvailable ? "#004f7d" : "text.secondary",
          }}
          onClick={() => {
            const target = routeActionTarget;
            onCloseRouteActionMenu();
            if (!target || !routeRenameAvailable) return;
            setRouteRenameTarget(target);
            setRouteRenameOpen(true);
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: routeRenameAvailable ? "#004f7d" : "text.secondary",
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="Rename"
            slotProps={{ primary: { sx: { fontSize: 13 } } }}
          />
        </MenuItem>
        {(() => {
          const route = routeActionTarget?.route;
          const isCandidate = route?.source === "draft";
          // Offer every state except the current one.
          const routeState =
            route?.state ?? (route?.active === false ? "archived" : "active");

          const runAction = (nextState) => {
            const target = routeActionTarget;
            onCloseRouteActionMenu();
            if (!target || target?.route?.source === "draft") return;
            onSetRouteState?.(target, nextState);
          };

          const itemSx = {
            fontSize: 13,
            color: isCandidate ? "text.secondary" : "#004f7d",
          };
          const iconSx = {
            minWidth: 28,
            color: isCandidate ? "text.secondary" : "#004f7d",
          };

          const STATE_ACTIONS = [
            { value: "active", label: "Set as Active", Icon: PlayCircleOutlineOutlinedIcon },
            { value: "suggested", label: "Set as Suggested", Icon: TipsAndUpdatesOutlinedIcon },
            { value: "archived", label: "Archive", Icon: ArchiveOutlinedIcon },
          ];

          return (
            <>
              {STATE_ACTIONS.filter(
                (a) => !isCandidate && a.value !== routeState,
              ).map(({ value, label, Icon }) => (
                <MenuItem
                  key={value}
                  sx={itemSx}
                  onClick={() => runAction(value)}
                >
                  <ListItemIcon sx={iconSx}>
                    <Icon sx={{ fontSize: 16 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    slotProps={{ primary: { sx: { fontSize: 13 } } }}
                  />
                </MenuItem>
              ))}
            </>
          );
        })()}
        <MenuItem
          disabled={routeActionTarget?.route?.source !== "draft"}
          sx={{
            fontSize: 13,
            color:
              routeActionTarget?.route?.source === "draft"
                ? "#004f7d"
                : "text.secondary",
          }}
          onClick={() => {
            const target = routeActionTarget;
            onCloseRouteActionMenu();
            if (!target || target?.route?.source !== "draft") return;
            setRouteSaveActiveVoyage(activeVoyage);
            setRouteSaveTarget(target);
            setRouteSaveOpen(true);
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 28,
              color:
                routeActionTarget?.route?.source === "draft"
                  ? "#004f7d"
                  : "text.secondary",
            }}
          >
            <SaveOutlinedIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="Save"
            slotProps={{ primary: { sx: { fontSize: 13 } } }}
          />
        </MenuItem>
        <MenuItem
          disabled={!routeExportAvailable}
          sx={{
            fontSize: 13,
            color: routeExportAvailable ? "#004f7d" : "text.secondary",
          }}
          onClick={() => {
            const target = routeActionTarget;
            onCloseRouteActionMenu();
            if (!target || !routeExportAvailable) return;
            setRouteExportTarget(target);
            // Set before opening: the Select must never render a value missing from its options.
            setRouteExportFormat(initialExportFormat(target.vessel));
            setRouteExportOpen(true);
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: routeExportAvailable ? "#004f7d" : "text.secondary",
            }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="Export"
            slotProps={{ primary: { sx: { fontSize: 13 } } }}
          />
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 13, color: "error.main" }}
          onClick={() => {
            const target = routeActionTarget;
            onCloseRouteActionMenu();
            if (!target) return;
            onOpenDeleteFromAction(target);
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: "error.main" }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{ primary: { sx: { fontSize: 13 } } }}
          />
        </MenuItem>
      </Menu>

      <RouteActionDialog
        open={routeSaveOpen}
        title="Save route"
        description={
          routeSaveVesselName
            ? `Save the calculated route for ${routeSaveVesselName}.`
            : "Save the calculated route."
        }
        routeName={routeSaveName}
        onRouteNameChange={setRouteSaveName}
        voyageMode="choose"
        vesselName={routeSaveVesselName}
        activeVoyageName={routeSaveActiveVoyage?.name ?? ""}
        saveTarget={routeSaveMode}
        onSaveTargetChange={setRouteSaveMode}
        voyageName={routeSaveVoyageName}
        onVoyageNameChange={setRouteSaveVoyageName}
        saving={routeSaveSaving}
        confirmLabel="Save"
        onCancel={() => { setRouteSaveOpen(false); onDialogClose?.(); }}
        onConfirm={async () => {
          const target = routeSaveTarget;
          if (!target) return;
          setRouteSaveSaving(true);
          try {
            await onSaveDraft?.(target.route, {
              routeName: routeSaveName,
              voyageName: routeSaveVoyageName,
              saveTarget: routeSaveMode,
              voyageId: routeSaveMode === "add" ? routeSaveActiveVoyage?.id : undefined,
            });
            setRouteSaveOpen(false);
            onDialogClose?.();
          } finally {
            setRouteSaveSaving(false);
          }
        }}
        disableConfirm={
          !routeSaveName.trim() ||
          (routeSaveMode === "new" && !routeSaveVoyageName.trim()) ||
          (routeSaveMode === "add" && !routeSaveActiveVoyage)
        }
      />

      <RouteActionDialog
        open={routeRenameOpen}
        title="Rename Route"
        description="Enter a new name for this route."
        routeName={routeRenameName}
        onRouteNameChange={setRouteRenameName}
        error={routeRenameError}
        saving={routeRenameSaving}
        confirmLabel="Save"
        onCancel={() => { setRouteRenameOpen(false); onDialogClose?.(); }}
        onConfirm={async () => {
          const target = routeRenameTarget;
          if (!target) return;
          setRouteRenameSaving(true);
          setRouteRenameError("");
          try {
            await onRenameRoute?.(target, routeRenameName);
            setRouteRenameOpen(false);
            onDialogClose?.();
          } catch (error) {
            setRouteRenameError(
              error instanceof Error ? error.message : "Rename failed.",
            );
          } finally {
            setRouteRenameSaving(false);
          }
        }}
        disableConfirm={!routeRenameName.trim()}
      />

      <RouteActionDialog
        open={routeExportOpen}
        title="Export Route"
        description="Choose a route name and export format."
        routeName={routeExportName}
        onRouteNameChange={setRouteExportName}
        showFormat
        format={routeExportFormat}
        formatOptions={exportFormatsForVessel(routeExportTarget?.vessel)}
        onFormatChange={setRouteExportFormat}
        error={routeExportError}
        saving={routeExportSaving}
        confirmLabel="Export"
        onCancel={() => { setRouteExportOpen(false); onDialogClose?.(); }}
        onConfirm={async () => {
          const target = routeExportTarget;
          if (!target) return;
          setRouteExportSaving(true);
          setRouteExportError("");
          try {
            await onExportRoute?.(target, {
              routeName: routeExportName,
              fileFormat: routeExportFormat,
            });
            setRouteExportOpen(false);
            onDialogClose?.();
          } catch (error) {
            setRouteExportError(
              error instanceof Error ? error.message : "Export failed.",
            );
          } finally {
            setRouteExportSaving(false);
          }
        }}
        disableConfirm={!routeExportName.trim()}
      />
    </>
  );
}
